import { Dialog, DialogPanel } from "@headlessui/react";
import { IoMdClose } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { commonToast } from "../../helper/Toast";
import Loading from "../Loading";
import {
  DEFAULT_ADMIN_PASSWORD,
  createParent,
  getApiError,
  mapPreferredLanguage,
} from "../../helper/adminUsersApi";

const COUNTRY_OPTIONS = [
  { value: "LT", label: "LT" },
  { value: "GB", label: "GB" },
  { value: "ES", label: "ES" },
];

export default function AddParentModel({ open, setOpen, onCreated }) {
  const { t, i18n } = useTranslation();

  const defaultCountry = String(i18n.language || "lt").toLowerCase().startsWith("es")
    ? "ES"
    : String(i18n.language || "lt").toLowerCase().startsWith("en")
      ? "GB"
      : "LT";

  const validationSchema = Yup.object({
    firstName: Yup.string().required(t("validation.name_required")),
    lastName: Yup.string().required(t("validation.lastname_required")),
    email: Yup.string()
      .email(t("validation.email_invalid"))
      .required(t("validation.email_required")),
    password: Yup.string().required(t("validation.password_required")),
    countryCode: Yup.string().required(t("country_code")),
  });

  const handleClose = () => {
    requestAnimationFrame(() => setOpen(false));
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      await createParent({
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        password: values.password,
        preferredLanguage: mapPreferredLanguage(i18n.language),
        countryCode: values.countryCode,
        userPreferences: {
          preferVisionImpairmentMode: false,
          allowMarketingDataCollection: false,
          allowAnalyticsDataCollection: false,
        },
      });

      commonToast(t("parent_management.add_success"), "success");
      onCreated?.();
      resetForm();
      handleClose();
    } catch (error) {
      commonToast(getApiError(error, t("parent_management.add_failed")), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      as="div"
      className="relative z-40 focus:outline-none"
      onClose={handleClose}
    >
      <div className="fixed inset-0 bg-black/40" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="w-full max-w-lg rounded-2xl bg-white p-6">
            <Formik
              key={open ? "parent-open" : "parent-closed"}
              initialValues={{
                firstName: "",
                lastName: "",
                email: "",
                password: DEFAULT_ADMIN_PASSWORD,
                countryCode: defaultCountry,
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900">
                      {t("parent_management.add_parent")}
                    </h3>
                    <IoMdClose
                      className="cursor-pointer text-gray-500 hover:text-gray-800"
                      fontSize={24}
                      onClick={handleClose}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-y-3">
                    <InputField label={t("first_name") || t("name")} name="firstName" />
                    <InputField label={t("last_name") || t("lastname")} name="lastName" />
                    <InputField label={t("email")} name="email" type="email" />
                    <InputField label={t("password")} name="password" type="password" />

                    <div>
                      <label
                        htmlFor="countryCode"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        {t("country_code")}
                      </label>
                      <Field
                        as="select"
                        name="countryCode"
                        className="border h-[45px] w-full rounded-lg px-3 shadow-inner outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 bg-white"
                      >
                        {COUNTRY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="countryCode"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-4">
                    <button type="button" className="secondary-btn" onClick={handleClose}>
                      {t("cancel")}
                    </button>
                    <button type="submit" className="primary-btn" disabled={isSubmitting}>
                      {isSubmitting ? <Loading size={5} /> : t("add") || "Add"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

const InputField = ({ label, name, type = "text" }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <Field
      type={type}
      name={name}
      autoComplete="off"
      className="border h-[45px] w-full rounded-lg px-3 shadow-inner outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
    />
    <ErrorMessage name={name} component="div" className="text-red-500 text-xs mt-1" />
  </div>
);
